/**
 * SportsCourtManagementPage — Live front-desk view.
 *
 * At-a-glance board for staff:
 *   - One card per active court
 *   - Now: current booking with live countdown timer (green >30m, yellow ≤30m, red ≤10m)
 *   - Up next: next 2-3 bookings for today
 *   - Top strip: facility clock + counters (in-use / available / total)
 *
 * Data:
 *   - GET /api/sports/courts (once)
 *   - GET /api/sports/bookings?date=YYYY-MM-DD (today, refreshed every 30s)
 *   - GET /api/sports/settings (for facility timezone)
 * Ticks every 1 second locally for smooth countdowns.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Radio, Clock, CircleCheck, CircleAlert, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import { fmt12, ymdTz, addDaysYmd } from '../utils/sportsUtils';
// K8 P2.5 P2: the urgency colours, countdown format and pulse keyframes are
// shared with the kiosk "machine in use" card so both live boards read as one.
import { urgency, fmtCountdown, LIVE_KEYFRAMES } from '../liveUi';

/* ── Elegant chime (Web Audio, no assets) ─────────────────────
 * Two staggered sine-wave notes with an exponential decay envelope
 * → a soft "ting … ting" bell reminiscent of hotel-desk chimes.
 */
const CHIME_KEY = 'sports_live_chime_muted_v1';
let _audioCtx = null;
function getAudioCtx() {
  if (typeof window === 'undefined') return null;
  if (_audioCtx && _audioCtx.state !== 'closed') return _audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _audioCtx = new Ctx();
  return _audioCtx;
}
function playChime() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const master = ctx.createGain();
    master.gain.value = 0.45;
    master.connect(ctx.destination);
    // Two notes: E6 then A6 (perfect fourth up) — soft & modern
    [
      { f: 1318.51, t: 0.00, dur: 1.6 },
      { f: 1760.00, t: 0.09, dur: 1.4 },
    ].forEach(({ f, t, dur }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      const start = ctx.currentTime + t;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.9, start + 0.015);       // fast attack
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur); // long decay
      osc.connect(g); g.connect(master);
      osc.start(start);
      osc.stop(start + dur + 0.05);
    });
  } catch {
    // ignore — audio is a nicety, not required
  }
}

const ACTIVE_STATUSES = new Set(['confirmed', 'walk_in', 'competition', 'awaiting_confirmation', 'hold']);

/* ── Time helpers ─────────────────────────────────────────────── */

// Parse "HH:MM" (24h, may exceed 24 to indicate next day) to total minutes.
// iOS Safari saves midnight as "00:00" where Chrome uses "24:00". As an
// END/close marker, "00:00" means end-of-day (1440), never 0.
function toMin(t) {
  if (!t) return 0;
  if (t === '00:00' || t === '0:00') return 1440;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

// Booking start/end in minutes relative to TODAY's clock. Overnight sessions
// fetched from yesterday carry _off = -1440 (their "26:00" is today's 02:00).
function bStart(b) { return toMin(b.start_time) + (b._off || 0); }
function bEnd(b)   { return toMin(b.end_time) + (b._off || 0); }

// Current minutes-of-day in the facility's timezone (0..1439).
function currentMinInTz(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const get = (k) => parseInt(parts.find(p => p.type === k)?.value || '0', 10);
    const h = get('hour') % 24;
    return h * 60 + get('minute') + get('second') / 60;
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
  }
}

// fmtCountdown lives in ../liveUi (shared with the kiosk live card).

function fmtStartsIn(mins) {
  if (mins <= 0) return 'now';
  if (mins < 1) return '<1 min';
  if (mins < 60) return `in ${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `in ${h}h ${m}m` : `in ${h}h`;
}

// Time-of-day clock for the top strip: "3:42 PM" in facility tz.
function facilityClock(tz) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(new Date());
  } catch {
    return new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
}

/* ── Colour bucket for countdown: urgency() lives in ../liveUi ─── */

function customerLabel(b) {
  return b.customer?.name || b.customer_name || b.player_name || b.contact_name || b.event_name || b.notes || 'Reserved';
}

/* ── Court card ───────────────────────────────────────────────── */
function CourtCard({ court, nowMin, bookings, onAction }) {
  // Filter bookings for this court that aren't cancelled/declined/expired
  const relevant = useMemo(() => bookings
    .filter(b => b.court_id === court.id && !['cancelled', 'declined', 'expired'].includes(b.status))
    .filter(b => ACTIVE_STATUSES.has(b.status))
    .map(b => ({ ...b, _s: bStart(b), _e: bEnd(b) }))
    .sort((a, b) => a._s - b._s),
  [bookings, court.id]);

  const active = relevant.find(b => b._s <= nowMin && b._e > nowMin);
  const upcoming = relevant.filter(b => b._s > nowMin).slice(0, 3);

  const minsLeft = active ? active._e - nowMin : 0;
  const u = active ? urgency(minsLeft) : null;
  const durationMin = active ? active._e - active._s : 0;
  const progress = active ? Math.min(100, Math.max(0, ((nowMin - active._s) / Math.max(1, durationMin)) * 100)) : 0;

  const idle = !active;

  return (
    <div
      data-testid={`live-court-card-${court.id}`}
      style={{
        position: 'relative',
        borderRadius: 14,
        background: idle ? '#0F0F0F' : `linear-gradient(180deg, ${u.bg}, #0D0D0D 60%)`,
        border: `1px solid ${idle ? '#1E1E1E' : u.border}`,
        boxShadow: idle ? 'none' : `0 0 24px -8px ${u.glow}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* Header */}
      <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, color: '#555', fontWeight: 700 }}>
              {court.sport_type || 'Court'}
            </p>
            <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: 0.5, textTransform: 'uppercase', lineHeight: 1.1, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              data-testid={`live-court-name-${court.id}`}>
              {court.name}
            </h3>
          </div>
          <span
            data-testid={`live-court-status-${court.id}`}
            style={{
              fontSize: 9, letterSpacing: 1.5, fontWeight: 800, textTransform: 'uppercase',
              padding: '4px 8px', borderRadius: 999, whiteSpace: 'nowrap',
              color: idle ? '#737373' : u.fg,
              background: idle ? 'rgba(115,115,115,0.10)' : u.bg,
              border: `1px solid ${idle ? 'rgba(115,115,115,0.25)' : u.border}`,
            }}>
            {idle ? <><CircleCheck className="inline h-3 w-3 -mt-0.5 mr-1" strokeWidth={2.5} />AVAILABLE</> : <><span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: u.fg,
              marginRight: 6, verticalAlign: 'middle',
              animation: u.key === 'red' ? 'sports-live-pulse 1s ease-in-out infinite' : 'none',
            }} />{u.label}</>}
          </span>
        </div>
      </div>

      {/* Now section */}
      <div style={{ padding: '16px', flex: 1 }}>
        {active ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, color: '#555', fontWeight: 700, marginBottom: 2 }}>Now Playing</p>
                <p style={{ color: '#fff', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  data-testid={`live-court-customer-${court.id}`}>
                  {customerLabel(active)}
                </p>
                <p style={{ fontSize: 12, color: '#A3A3A3', marginTop: 2 }}>
                  {fmt12(active.start_time)} – {fmt12(active.end_time)}
                </p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, color: '#555', fontWeight: 700, marginBottom: 2 }}>Time Left</p>
                <div
                  data-testid={`live-court-countdown-${court.id}`}
                  style={{
                    fontFamily: 'Oswald, sans-serif',
                    fontSize: 32, fontWeight: 800,
                    color: u.fg,
                    lineHeight: 1,
                    letterSpacing: 1,
                    textShadow: `0 0 18px ${u.glow}`,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                  {fmtCountdown(minsLeft)}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{
              height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', marginTop: 4,
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: u.fg,
                boxShadow: `0 0 10px ${u.glow}`,
                transition: 'width 0.5s linear',
              }} />
            </div>

            {/* Front-desk actions */}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button
                type="button"
                data-testid={`live-end-session-${court.id}`}
                onClick={() => onAction('end', active, court)}
                style={{
                  flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  background: 'rgba(255,45,85,0.08)', border: '1px solid rgba(255,45,85,0.35)',
                  color: '#FF2D55', fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase',
                }}>
                End Session
              </button>
              <button
                type="button"
                data-testid={`live-no-show-${court.id}`}
                onClick={() => onAction('no_show', active, court)}
                style={{
                  flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  background: 'rgba(255,179,0,0.07)', border: '1px solid rgba(255,179,0,0.35)',
                  color: '#FFB300', fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase',
                }}>
                No-Show
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '18px 0 10px', textAlign: 'center' }}>
            <div style={{
              height: 44, width: 44, borderRadius: 999,
              background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
            }}>
              <CircleCheck className="h-5 w-5" style={{ color: '#22C55E' }} strokeWidth={2.4} />
            </div>
            <p style={{ color: '#A3A3A3', fontSize: 13, fontWeight: 600 }}>Court is open</p>
            <p style={{ color: '#555', fontSize: 11, marginTop: 2 }}>Ready for walk-ins</p>
          </div>
        )}
      </div>

      {/* Up next */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '12px 16px', background: 'rgba(255,255,255,0.02)' }}>
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, color: '#555', fontWeight: 700, marginBottom: 8 }}>
          Up Next{upcoming.length ? ` · ${upcoming.length}` : ''}
        </p>
        {upcoming.length === 0 ? (
          <p style={{ fontSize: 12, color: '#3F3F3F' }}>No further bookings today.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {upcoming.map(b => {
              const startsInMin = b._s - nowMin;
              return (
                <div key={b.id}
                  data-testid={`live-court-upcoming-${court.id}-${b.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, minWidth: 0 }}>
                  <Clock className="h-3.5 w-3.5" style={{ color: '#555', flexShrink: 0 }} />
                  <span style={{ color: '#D4D4D4', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {fmt12(b.start_time)}{b.end_time ? ` – ${fmt12(b.end_time)}` : ''}
                  </span>
                  <span style={{ color: '#525252', whiteSpace: 'nowrap' }}>·</span>
                  <span style={{ color: '#A3A3A3', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                    {customerLabel(b)}
                  </span>
                  <span style={{
                    color: startsInMin <= 15 ? '#FFB300' : '#737373',
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', flexShrink: 0,
                  }}>
                    {fmtStartsIn(startsInMin)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────── */
export default function SportsCourtManagementPage() {
  const [facilityTz, setFacilityTz] = useState('Asia/Manila');
  const [courts, setCourts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loadedOnce, setLoadedOnce] = useState(false);

  // Front-desk action confirmation: { type: 'end' | 'no_show', booking, court }
  const [confirm, setConfirm] = useState(null);
  const [acting, setActing] = useState(false);

  // Tick every 1s → drives clock + all countdowns.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Mute toggle for the end-of-booking chime (persisted per browser).
  const [muted, setMuted] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage?.getItem(CHIME_KEY) === '1';
  });
  const toggleMute = () => {
    setMuted(prev => {
      const next = !prev;
      try { window.localStorage.setItem(CHIME_KEY, next ? '1' : '0'); } catch { /* noop */ }
      // Also warm the AudioContext on the "unmute" click so first chime plays without user-gesture blockage
      if (!next) { try { const ctx = getAudioCtx(); if (ctx?.state === 'suspended') ctx.resume(); } catch { /* noop */ } }
      return next;
    });
  };

  // Track which bookings we've already alerted for, so each court chimes exactly once per booking.
  const alertedRef = useRef(new Set());
  // Also remember which bookings were "active last tick" so we know the moment they cross into the ended zone.
  const wasActiveRef = useRef(new Set());

  // Load static: courts + settings (timezone)
  useEffect(() => {
    sportsApi.get('/sports/courts').then(r => setCourts(r.data || [])).catch(() => {});
    sportsApi.get('/sports/settings').then(r => {
      const tz = r.data?.timezone || 'Asia/Manila';
      setFacilityTz(tz);
    }).catch(() => {});
  }, []);

  // Refresh bookings every 30s (today + yesterday's overnight sessions).
  // Uses todayInTz recomputed each fetch so the board rolls automatically.
  const fetchBookings = useCallback(() => {
    const today = ymdTz(facilityTz);
    Promise.all([
      sportsApi.get('/sports/bookings', { params: { date: today } }).catch(() => ({ data: [] })),
      sportsApi.get('/sports/bookings', { params: { date: addDaysYmd(today, -1) } }).catch(() => ({ data: [] })),
    ]).then(([t, y]) => {
      // Yesterday's sessions that cross midnight (end_time > 24:00, e.g. a
      // court closing at "27:00" = 3 AM) are still live on today's board.
      const overnight = (y.data || [])
        .filter(b => toMin(b.end_time) > 1440)
        .map(b => ({ ...b, _off: -1440 }));
      setBookings([...overnight, ...(t.data || [])]);
    }).finally(() => setLoadedOnce(true));
  }, [facilityTz]);

  useEffect(() => {
    fetchBookings();
    const id = setInterval(fetchBookings, 30_000);
    return () => clearInterval(id);
  }, [fetchBookings]);

  // Confirmed front-desk action: end session early OR mark no-show.
  const runConfirmedAction = async () => {
    if (!confirm || acting) return;
    const { type, booking } = confirm;
    setActing(true);
    try {
      if (type === 'end') {
        const r = await sportsApi.post(`/sports/bookings/${booking.id}/end-now`);
        alertedRef.current.add(booking.id); // manual end — skip the "time's up" chime
        toast.success('Session ended — court is free', {
          description: `${customerLabel(booking)} ended at ${fmt12(r.data.end_time)}. The remaining time is open for walk-ins.`,
          duration: 7000,
        });
      } else {
        await sportsApi.post(`/sports/bookings/${booking.id}/cancel`, { reason: 'no_show' });
        alertedRef.current.add(booking.id);
        toast.success('Marked as no-show', {
          description: `${customerLabel(booking)}'s booking was cancelled and the whole slot is bookable again.`,
          duration: 7000,
        });
      }
      setConfirm(null);
      fetchBookings();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Action failed — please try again'));
    } finally {
      setActing(false);
    }
  };

  const activeCourts = useMemo(() => courts.filter(c => c.active), [courts]);
  const nowMin = useMemo(() => currentMinInTz(facilityTz), [facilityTz, nowTick]);

  // Detect "time's up" transitions: a booking that was active in the previous tick
  // and whose end_time has now passed → play chime + toast, exactly once.
  useEffect(() => {
    if (!bookings.length || !activeCourts.length) return;
    const courtNameById = Object.fromEntries(activeCourts.map(c => [c.id, c.name]));
    const nowActiveIds = new Set();
    const justEnded = [];
    for (const b of bookings) {
      if (!ACTIVE_STATUSES.has(b.status)) continue;
      if (!courtNameById[b.court_id]) continue;
      const s = bStart(b);
      const e = bEnd(b);
      const isActive = s <= nowMin && e > nowMin;
      const hasEnded = e <= nowMin && wasActiveRef.current.has(b.id);
      if (isActive) nowActiveIds.add(b.id);
      if (hasEnded && !alertedRef.current.has(b.id)) {
        justEnded.push({ id: b.id, court: courtNameById[b.court_id], customer: customerLabel(b) });
        alertedRef.current.add(b.id);
      }
    }
    wasActiveRef.current = nowActiveIds;

    if (justEnded.length) {
      // One chime + one toast per court that just wrapped.
      if (!muted) playChime();
      justEnded.forEach(({ id, court, customer }) => {
        toast.success(`Time's up — ${court}`, {
          description: `${customer}'s session just ended. Ready for next reservation.`,
          duration: 8000,
          id: `live-end-${id}`, // idempotent — won't stack duplicates
        });
      });
    }
  }, [bookings, activeCourts, nowMin, muted]);

  // Counters
  const { inUse, available } = useMemo(() => {
    let inUseCount = 0;
    for (const c of activeCourts) {
      const b = bookings.find(x => x.court_id === c.id &&
        ACTIVE_STATUSES.has(x.status) &&
        bStart(x) <= nowMin && bEnd(x) > nowMin);
      if (b) inUseCount++;
    }
    return { inUse: inUseCount, available: activeCourts.length - inUseCount };
  }, [activeCourts, bookings, nowMin]);

  // Any court with a red-urgency booking → show pulsing "attention" marker
  const anyEndingSoon = useMemo(() => {
    for (const c of activeCourts) {
      const b = bookings.find(x => x.court_id === c.id &&
        ACTIVE_STATUSES.has(x.status) &&
        bStart(x) <= nowMin && bEnd(x) > nowMin);
      if (b && (bEnd(b) - nowMin) <= 10) return true;
    }
    return false;
  }, [activeCourts, bookings, nowMin]);

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />

      {/* Keyframes for the pulsing indicators — shared with the kiosk live card */}
      <style>{LIVE_KEYFRAMES}</style>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 16px 40px' }}>
        {/* ── Header strip ──────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 3, color: '#333', marginBottom: 4 }}>Front Desk</p>
            <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(24px, 4.4vw, 34px)', fontWeight: 900, textTransform: 'uppercase', color: '#fff', lineHeight: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 12 }}
              data-testid="live-page-title">
              <span style={{
                display: 'inline-flex', width: 10, height: 10, borderRadius: 999, background: '#E63946',
                animation: 'sports-live-dot 1.6s ease-out infinite',
              }} />
              Court Management
            </h1>
            <p style={{ fontSize: 12, color: '#525252', marginTop: 6, letterSpacing: 0.3 }}>
              Live view · updates every second · bookings refresh every 30s
            </p>
          </div>

          {/* Stat pills */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatPill label="Facility Time" value={facilityClock(facilityTz)} accent="#fff" testId="live-facility-clock" />
            <StatPill label="In Use" value={inUse} accent="#E63946" testId="live-stat-in-use" />
            <StatPill label="Available" value={available} accent="#22C55E" testId="live-stat-available" />
            <StatPill label="Total" value={activeCourts.length} accent="#A3A3A3" testId="live-stat-total" />

            {/* Chime mute toggle — click to enable audio on first use (browser gesture requirement) */}
            <button
              type="button"
              onClick={() => { toggleMute(); if (muted) { setTimeout(playChime, 60); } }}
              data-testid="live-chime-toggle"
              title={muted ? 'End-of-session chime is OFF — click to enable' : 'End-of-session chime is ON — click to mute'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '9px 12px', borderRadius: 10, cursor: 'pointer',
                background: muted ? '#0F0F0F' : 'rgba(230,57,70,0.10)',
                border: `1px solid ${muted ? '#1E1E1E' : 'rgba(230,57,70,0.4)'}`,
                color: muted ? '#737373' : '#E63946',
                fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase',
                transition: 'background 0.15s, color 0.15s, border 0.15s',
              }}>
              {muted ? <VolumeX className="h-4 w-4" strokeWidth={2.4} /> : <Volume2 className="h-4 w-4" strokeWidth={2.4} />}
              {muted ? 'Chime Off' : 'Chime On'}
            </button>
            {anyEndingSoon && (
              <div data-testid="live-attention-banner"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', borderRadius: 10,
                  background: 'rgba(255,45,85,0.10)', border: '1px solid rgba(255,45,85,0.4)',
                  color: '#FF2D55', fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                }}>
                <CircleAlert className="h-4 w-4" strokeWidth={2.4} />
                Court(s) wrapping up
              </div>
            )}
          </div>
        </div>

        {/* ── Empty / Content ───────────────────────────────────── */}
        {!loadedOnce ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#3F3F3F', fontSize: 13 }}>
            Loading live view…
          </div>
        ) : activeCourts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', border: '1px dashed #1E1E1E', borderRadius: 14, color: '#525252' }}>
            <Radio className="h-8 w-8 mx-auto mb-3" style={{ color: '#333' }} />
            <p style={{ fontSize: 14, fontWeight: 700, color: '#A3A3A3' }}>No active courts</p>
            <p style={{ fontSize: 12, marginTop: 4 }}>Add or activate a court under <a href="/sports/courts" style={{ color: '#E63946' }}>My Courts</a> to see it here.</p>
          </div>
        ) : (
          <div
            data-testid="live-court-grid"
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            }}>
            {activeCourts.map(c => (
              <CourtCard key={c.id} court={c} nowMin={nowMin} bookings={bookings}
                onAction={(type, booking, court) => setConfirm({ type, booking, court })} />
            ))}
          </div>
        )}

        {/* ── Legend ────────────────────────────────────────────── */}
        {activeCourts.length > 0 && (
          <div style={{ marginTop: 26, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', fontSize: 11, color: '#525252' }}
            data-testid="live-legend">
            <span style={{ letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, color: '#333' }}>Legend</span>
            <LegendChip color="#22C55E" label=">30 min left" />
            <LegendChip color="#FFB300" label="≤ 30 min" />
            <LegendChip color="#FF2D55" label="≤ 10 min" />
            <LegendChip color="#737373" label="Available" outline />
          </div>
        )}
      </div>

      {/* ── Confirm dialog for front-desk actions ─────────────── */}
      {confirm && (
        <div
          data-testid="live-confirm-dialog"
          onClick={() => !acting && setConfirm(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 420, background: '#111', border: '1px solid #262626', borderRadius: 14, padding: 22 }}>
            <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 800, textTransform: 'uppercase', color: confirm.type === 'end' ? '#FF2D55' : '#FFB300', letterSpacing: 0.5 }}>
              {confirm.type === 'end' ? 'End this session early?' : 'Mark as no-show?'}
            </h3>
            <p style={{ fontSize: 13, color: '#A3A3A3', marginTop: 10, lineHeight: 1.5 }}>
              {confirm.type === 'end' ? (
                <>The timer for <b style={{ color: '#fff' }}>{customerLabel(confirm.booking)}</b> on <b style={{ color: '#fff' }}>{confirm.court.name}</b> stops now. Their booking is kept (paid time stands) and the <b style={{ color: '#fff' }}>remaining hours open up instantly</b> for a walk-in or Play Now booking.</>
              ) : (
                <>The booking for <b style={{ color: '#fff' }}>{customerLabel(confirm.booking)}</b> on <b style={{ color: '#fff' }}>{confirm.court.name}</b> will be <b style={{ color: '#fff' }}>cancelled as a no-show</b> and the entire time slot becomes bookable again.</>
              )}
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button type="button" data-testid="live-confirm-no" disabled={acting}
                onClick={() => setConfirm(null)}
                style={{ flex: 1, padding: '10px', borderRadius: 9, cursor: 'pointer', background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#A3A3A3', fontSize: 12, fontWeight: 700 }}>
                Keep Session
              </button>
              <button type="button" data-testid="live-confirm-yes" disabled={acting}
                onClick={runConfirmedAction}
                style={{ flex: 1, padding: '10px', borderRadius: 9, cursor: acting ? 'wait' : 'pointer', background: confirm.type === 'end' ? 'rgba(255,45,85,0.15)' : 'rgba(255,179,0,0.13)', border: `1px solid ${confirm.type === 'end' ? 'rgba(255,45,85,0.5)' : 'rgba(255,179,0,0.5)'}`, color: confirm.type === 'end' ? '#FF2D55' : '#FFB300', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                {acting ? 'Working…' : (confirm.type === 'end' ? 'End Session' : 'Confirm No-Show')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sub components ───────────────────────────────────────────── */
function StatPill({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      padding: '8px 14px', borderRadius: 10,
      background: '#0F0F0F', border: '1px solid #1E1E1E', minWidth: 82,
    }}>
      <span style={{ fontSize: 9, letterSpacing: 1.6, textTransform: 'uppercase', color: '#555', fontWeight: 700 }}>{label}</span>
      <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 800, color: accent, letterSpacing: 0.5, lineHeight: 1.1, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

function LegendChip({ color, label, outline }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 10, height: 10, borderRadius: 3,
        background: outline ? 'transparent' : color,
        border: outline ? `1px solid ${color}` : 'none',
      }} />
      {label}
    </span>
  );
}
